import React, { useState, useMemo } from 'react';
import _get from 'lodash/get';
import * as Types from 'pages/Pages/typings';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import { BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION } from 'pages/Attributes/consts';

import CancelButton from 'components/Page/CancelButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AttributeTypeCard, { attributeTypes } from 'pages/AttributesNew/AttributeTypeCard';
import SlideContent from './SlideContent';
import LoadingButton from 'atoms/LoadingButton';

interface Props {
  classes: any;
  loading: boolean;
  selectedAttribute?: typeof BASE_FIELD_PAGE | typeof BASE_FIELD_PAGE_COLLECTION;
  onSave: (data: Types.NewEntityType) => void;
  onSetOpen: (status: boolean) => void;
  setSlideIndex: (slideIndex: number) => void;
}

function NewAttributeSlide({
  classes,
  loading,
  selectedAttribute,
  onSave,
  onSetOpen,
  setSlideIndex,
}: Props) {
  const location = useLocation();
  const search = location.search;
  const query = useMemo(() => queryString.parse(search), [location.search]);
  const parentObjectKey = _get(query, 'parentObjectKey');
  const [newAttributeName, setNewAttributeName] = useState<string | undefined>('');
  const [newAttributeType, setNewAttributeType] = useState<
    typeof BASE_FIELD_PAGE | typeof BASE_FIELD_PAGE_COLLECTION | null
  >(selectedAttribute || null);
  const error =
    Boolean(newAttributeType) &&
    (!Boolean(newAttributeName) ||
      (typeof newAttributeName === 'string' && newAttributeName.trim() === ''));
  let pageItem = _get(attributeTypes, BASE_FIELD_PAGE);
  let pageCollectionItem = _get(attributeTypes, BASE_FIELD_PAGE_COLLECTION);

  const handleChangeNewAttributeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAttributeName(e.target.value);
  };

  const handleCreateNewAttribute = () => {
    newAttributeName &&
      onSave({
        name: newAttributeName,
        newPersonFieldType: newAttributeType,
        convertPersonField: null,
        parentFieldId: Boolean(parentObjectKey) ? 'user_reachOutToMeFor' : null,
      });
  };

  return (
    <SlideContent
      actions={
        <>
          <CancelButton
            onClick={() => {
              newAttributeType ? onSetOpen(false) : setSlideIndex(1);
            }}
          />
          <LoadingButton
            color="secondary"
            disabled={newAttributeName === '' || !newAttributeType}
            loading={loading}
            label="Create Attribute (page type)"
            onClick={handleCreateNewAttribute}
          />
        </>
      }
      renderBackIcon
      renderCloseIcon
      lastSlideIndex={1}
      resetIndex={newAttributeType ? 4 : 0}
      slideTitle="Create a New Attribute (Page Type)"
      onSetOpen={onSetOpen}
      setSlideIndex={setSlideIndex}
    >
      <Typography variant="h6">Attribute (Page Type) Name</Typography>
      <Typography variant="subtitle2">
        What type of things do these pages represent? For example: 'Project', 'Award', etc.
      </Typography>
      <TextField
        className={classes.marginBottom2}
        error={error}
        fullWidth={true}
        id="new-page-type-name-input"
        value={newAttributeName}
        onChange={handleChangeNewAttributeName}
        inputProps={{ 'aria-label': 'new page type name input' }}
      />
      <Typography variant="h6" className={classes.marginBottom}>
        Attribute Data Type
      </Typography>
      <div className={classes.cardDiv}>
        <AttributeTypeCard
          className={newAttributeType === BASE_FIELD_PAGE ? classes.activeCard : undefined}
          item={pageItem}
          onClick={() => setNewAttributeType(BASE_FIELD_PAGE)}
          link={false}
        />
        <AttributeTypeCard
          className={
            newAttributeType === BASE_FIELD_PAGE_COLLECTION ? classes.activeCard : undefined
          }
          item={pageCollectionItem}
          link={false}
          onClick={() => setNewAttributeType(BASE_FIELD_PAGE_COLLECTION)}
        />
      </div>
    </SlideContent>
  );
}

export default NewAttributeSlide;
